import { Table } from "@tanstack/react-table";
import { map } from "lodash";
import { BookingCalendar } from "PFApp/booking/types";
import BulkSelect from "PFApp/components/table/bulk_select";
import { useGrowl } from "PFApp/use_growl";
import { Button } from "PFComponents/button";
import InlineCalendar from "PFComponents/calendar/inline_calendar";
import Select from "PFComponents/select/select";
import { useAddBookingConfigProfileCalendars } from "PFCore/hooks/queries/bookings/booking_config/use_add_booking_config_profile_calendar";
import { useBookingConfigCalendars } from "PFCore/hooks/queries/bookings/booking_config/use_booking_config_calendars";
import { useBookingConfigProfileCalendars } from "PFCore/hooks/queries/bookings/booking_config/use_booking_config_profile_calendars";
import { useBookingConfigProfileCalendarsInvalidate } from "PFCore/hooks/queries/bookings/booking_config/use_booking_config_profile_calendars_invalidate";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { WorkforceRowData } from "../types";
import css from "../workforce.module.scss";

type ToolbarProps = {
  tableInstance: Table<WorkforceRowData>;
  setProfileRowError: (profileId: number, error: JQueryXHR) => void;
  cleanProfileRowErrors: (profileId: number) => void;
};

export const Toolbar = ({ tableInstance, setProfileRowError, cleanProfileRowErrors }: ToolbarProps) => {
  const { t } = useTranslation("bookingModule");
  const growl = useGrowl();
  const { formatISODate } = useDateFormatter();

  const [selectedCalendar, setSelectedCalendar] = useState<BookingCalendar | null>(null);
  const [startDate, setStartDate] = useState(null);
  const { invalidate: invalidateProfileCalendars } = useBookingConfigProfileCalendarsInvalidate();

  const { data } = useBookingConfigCalendars();
  const calendars = data?.entries || [];
  const { data: profileCalendars } = useBookingConfigProfileCalendars();
  const { mutateAsync: addCalendar } = useAddBookingConfigProfileCalendars();

  const handleSelectAll = useCallback(() => tableInstance.toggleAllRowsSelected(true), [tableInstance]);
  const handleSelectNone = useCallback(() => tableInstance.toggleAllRowsSelected(false), [tableInstance]);

  // for some reason getIsSomeRowsSelected() returns false when all rows are selected
  const isAnyRowSelected = tableInstance.getIsSomeRowsSelected() || tableInstance.getIsAllRowsSelected();

  useEffect(() => {
    if (!isAnyRowSelected) {
      setSelectedCalendar(null);
      setStartDate(null);
    }
  }, [isAnyRowSelected]);

  const handleAssign = async () => {
    const selectedProfiles = tableInstance
      .getSelectedRowModel()
      .flatRows.map((row) => row.original.profile.id);

    const assignPromises = selectedProfiles.map((profileId) =>
      addCalendar({ profileId, calendarId: selectedCalendar?.id, startDate }).then(
        () => cleanProfileRowErrors(profileId),
        (error) => {
          setProfileRowError(profileId, error);
          throw error;
        }
      )
    );

    await Promise.all(assignPromises).then(
      () =>
        growl({
          kind: "success",
          message: t("config.calendars.workforce.addedToCalendar")
        }),
      () =>
        growl({
          kind: "error",
          message: t("config.calendars.workforce.addedToCalendarError")
        })
    );

    invalidateProfileCalendars();
  };

  return (
    <div className={css.workforceToolbar}>
      <BulkSelect
        selectedItems={map(tableInstance.getSelectedRowModel().flatRows, "original.profile")}
        onSelectAll={handleSelectAll}
        onSelectNone={handleSelectNone}
        allItems={map(profileCalendars?.entries, "profile") || []}
        displayLabel
      />
      <div className={css.headerWrapper}>
        <div className={css.dateSelectHeader}>
          <span>{t("config.calendars.workforce.calendar")}</span>
          <Select
            /*@ts-ignore-line */
            controlledValue
            disabled={!isAnyRowSelected}
            placeholder={t("config.calendars.workforce.selectCalendar")}
            options={calendars.map(({ id, name }) => ({ id, displayElement: name, item: id }))}
            handleChange={(value) => setSelectedCalendar(calendars.find(({ id }) => id === value) ?? null)}
            value={selectedCalendar?.name}
          />
        </div>
        <div className={css.dateSelectHeader}>
          <span>{t("config.calendars.workforce.start")}</span>
          <InlineCalendar
            calendarStyle={{}}
            placeholder="Select Date"
            handleChange={setStartDate}
            selectedDate={startDate}
            minDate={formatISODate()}
            name="start_date"
          />
        </div>
        <Button kind="tertiary" onClick={handleAssign} disabled={!isAnyRowSelected || !selectedCalendar}>
          {t("config.calendars.workforce.addToCalendar")}
        </Button>
      </div>
    </div>
  );
};
